import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import Clarity from '@microsoft/clarity';
import { filter } from 'rxjs/operators';
import { EnvService } from '../../env.service';
import { SessionVariable } from '../enum/session-variable';
import { LocalStorageService } from './local-storage.service';

export interface ClarityIdentity {
  userId?: string;
  name?: string;
  phone?: string;
  civilId?: string;
  email?: string;
  orderId?: string;
  orderToken?: string;
}

export type ClarityIdentityField = keyof ClarityIdentity;

type ClarityIdentityInput = {
  [K in ClarityIdentityField]?: unknown;
};

interface ProfileIdentitySource {
  id?: string | number;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  emailAddress?: string;
  nationalId?: string;
  fullName?: string;
  fullNameArabic?: string;
}

interface PaymentInfoIdentitySource {
  id?: string | number;
  orderToken?: string;
  userId?: string | number;
  customerMobileNumber?: string;
  customerName?: string;
}

interface SignupInitIdentitySource {
  mobileNumber?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private router = inject(Router);
  private envService = inject(EnvService);
  private storageService = inject(LocalStorageService);
  private projectId: string | null = null;
  private initialized = false;
  private identity: ClarityIdentity = {};
  private lastPushedSignature = '';

  constructor() {
    this.projectId = this.envService.ClarityProjectId || null;
    if (this.projectId) {
      this.initIfAllowed(this.currentUrl());
      this.syncRecordingWithRoute();
    } else {
      console.warn('ClarityService: Project ID is empty. Clarity tracking is disabled.');
    }
  }

  applyIdentity(partial: ClarityIdentityInput): void {
    const incomingOrderToken = this.toTagValue(partial.orderToken);
    const previousOrderToken = this.identity.orderToken;
    const orderTokenChanged =
      !!incomingOrderToken && !!previousOrderToken && incomingOrderToken !== previousOrderToken;

    if (orderTokenChanged) {
      this.restartRecordingForNewOrder();
    }

    const next: ClarityIdentity = orderTokenChanged
      ? this.withoutOrderFields(this.identity)
      : { ...this.identity };
    let changed = orderTokenChanged;

    (Object.keys(partial) as ClarityIdentityField[]).forEach((key) => {
      const value = this.toTagValue(partial[key]);
      if (value && next[key] !== value) {
        next[key] = value;
        changed = true;
      }
    });

    if (!changed) {
      return;
    }

    this.identity = next;
    this.pushToClarity();
  }

  captureApiResponse(url: string, responseBody: unknown, requestBody?: unknown): void {
    if (!this.projectId || !this.isCheckoutRoute(this.currentUrl())) {
      return;
    }

    if (this.isSignupInitUrl(url)) {
      this.identifyFromSignupInit(responseBody, requestBody);
      return;
    }

    if (this.isUserProfileUrl(url)) {
      this.identifyFromProfile(responseBody);
      return;
    }

    if (this.isPaymentInfoUrl(url)) {
      this.identifyFromPaymentInfo(responseBody);
    }
  }

  identifyFromSignupInit(responseBody: unknown, requestBody?: unknown): void {
    const response = this.asRecord<SignupInitIdentitySource>(responseBody);
    const request = this.asRecord<SignupInitIdentitySource>(requestBody);

    this.applyIdentity({ phone: response?.mobileNumber || request?.mobileNumber });
  }

  identifyFromProfile(responseBody: unknown): void {
    const profile = this.asRecord<ProfileIdentitySource>(responseBody);
    if (!profile) {
      return;
    }

    this.applyIdentity({
      userId: profile.id,
      name: this.resolveName(profile),
      phone: profile.mobileNumber,
      civilId: profile.nationalId,
      email: profile.emailAddress,
    });
  }

  identifyFromPaymentInfo(responseBody: unknown): void {
    const order = this.asRecord<PaymentInfoIdentitySource>(responseBody);
    if (!order) {
      return;
    }

    this.applyIdentity({
      userId: order.userId,
      name: order.customerName,
      phone: order.customerMobileNumber,
      orderId: order.id,
      orderToken: order.orderToken,
    });
  }

  syncFromStorage(): void {
    if (!this.projectId || !this.isCheckoutRoute(this.currentUrl())) {
      return;
    }

    const profile = this.readAuthProfile();

    this.applyIdentity({
      userId: this.readStorageValue(SessionVariable.USER_ID) || profile?.id,
      name: this.resolveName(profile),
      phone:
        this.readStorageValue(SessionVariable.MOBILE_NUMBER) ||
        profile?.mobileNumber ||
        this.readStorageValue(SessionVariable.REGISTRATION_NUMBER),
      civilId: this.readStorageValue(SessionVariable.NATIONAL_ID) || profile?.nationalId,
      email: profile?.emailAddress,
      orderId: this.readStorageValue(SessionVariable.ORDER_ID_FOR_CNET),
      orderToken: this.readStorageValue(SessionVariable.ORDER_ID),
    });
  }

  private currentUrl(): string {
    const routerUrl = this.router.url || '';
    const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    return `${routerUrl} ${path}`;
  }

  private isCheckoutRoute(url: string): boolean {
    return url.includes('/secure-checkout');
  }

  private initIfAllowed(url: string): void {
    if (!this.projectId || this.initialized || !this.isCheckoutRoute(url)) {
      return;
    }

    Clarity.init(this.projectId);
    this.initialized = true;
    this.syncFromStorage();
    this.pushToClarity();
  }

  private syncRecordingWithRoute(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        if (!this.isCheckoutRoute(url)) {
          this.stopRecording();
          return;
        }

        if (!this.initialized) {
          this.initIfAllowed(url);
          return;
        }

        this.startRecording();
        this.syncFromStorage();
      });
  }

  private stopRecording(): void {
    this.callClarity('stop');
  }

  private startRecording(): void {
    this.callClarity('start');
  }

  private restartRecordingForNewOrder(): void {
    if (!this.initialized) {
      return;
    }

    this.stopRecording();
    this.startRecording();
    this.lastPushedSignature = '';
  }

  private withoutOrderFields(identity: ClarityIdentity): ClarityIdentity {
    return {
      userId: identity.userId,
      name: identity.name,
      phone: identity.phone,
      civilId: identity.civilId,
      email: identity.email,
    };
  }

  private pushToClarity(): void {
    if (!this.initialized || !this.isCheckoutRoute(this.currentUrl())) {
      return;
    }

    const signature = JSON.stringify(this.identity);
    if (signature === this.lastPushedSignature) {
      return;
    }
    this.lastPushedSignature = signature;

    const customId = this.identity.userId || this.identity.phone || this.identity.civilId;
    if (customId) {
      Clarity.identify(customId, undefined, undefined, this.identity.name);
    }

    (Object.keys(this.identity) as ClarityIdentityField[]).forEach((key) => {
      const value = this.identity[key];
      if (value) {
        Clarity.setTag(key, value);
      }
    });
  }

  private callClarity(command: 'start' | 'stop'): void {
    const win = window as Window & { clarity?: (command: string) => void };
    if (typeof win.clarity === 'function') {
      win.clarity(command);
    }
  }

  private resolveName(source?: ProfileIdentitySource | null): string | undefined {
    if (!source) {
      return undefined;
    }

    const fullName = this.toTagValue(source.fullName);
    if (fullName) {
      return fullName;
    }

    const combined = [source.firstName, source.lastName]
      .map((part) => this.toTagValue(part))
      .filter((part): part is string => !!part)
      .join(' ');
    if (combined) {
      return combined;
    }

    return this.toTagValue(source.fullNameArabic);
  }

  private readAuthProfile(): ProfileIdentitySource | null {
    return this.parseStoredValue<ProfileIdentitySource>(
      this.storageService.getItem(SessionVariable.AUTH_PROFILE)
    );
  }

  private readStorageValue(key: SessionVariable): string | undefined {
    return this.toTagValue(this.storageService.getItem(key));
  }

  private parseStoredValue<T>(value: unknown): T | null {
    if (value == null || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      return value as T;
    }

    if (typeof value === 'string') {
      try {
        return this.parseStoredValue<T>(JSON.parse(value));
      } catch {
        return null;
      }
    }

    return null;
  }

  private asRecord<T>(value: unknown): T | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    return value as T;
  }

  private toTagValue(value: unknown): string | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    const normalized = String(value).trim();
    if (!normalized || normalized === 'null' || normalized === 'undefined') {
      return undefined;
    }

    return normalized;
  }

  private isSignupInitUrl(url: string): boolean {
    return url.includes('/accounts/signup/init');
  }

  private isUserProfileUrl(url: string): boolean {
    const path = this.pathnameOf(url);
    return path.endsWith('/accounts/profile');
  }

  private isPaymentInfoUrl(url: string): boolean {
    return /\/accounts\/payment\/info(\/|$|\?)/.test(url);
  }

  private pathnameOf(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
    } catch {
      return url.split('?')[0].replace(/\/+$/, '');
    }
  }
}
